import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Event,
  EventType,
  FormInputType,
  IntegrationConnectionStatus,
  NotificationType,
  TicketType,
  Visibility,
} from "@markit/common.types";
import { API } from "../../API";
import { useTheme } from "../../hooks/useTheme";
import { AppState } from "../../redux/store";
import { accountActions } from "../../redux/slices/accountSlice";
import { showNotificationBanner } from "../../utils/notificationUtils";
import IOSSwitch from "../IOSSwitch";
import { Icon } from "@iconify/react";
import { DropdownMenu, DropdownMenuItem } from "../../components/DropdownMenu";
import ThemeDropdownToggle from "../CreatorDashboard/Products/Components/ThemeDropdownToggle";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import { Colors } from "../../utils/colors";
import ConfirmActionModal from "../Containers/ConfirmPopups/ConfirmActionModal";
import { NavigationId } from "../../navigation/AppParamList";

interface CreateEventExtraDetailsProps {
  eventSettings: Event;
  updateEventSettings: (update: Partial<Event>) => void;
}

const CreateEventExtraDetails = (props: CreateEventExtraDetailsProps) => {
  const { eventSettings, updateEventSettings } = props;
  const { theme } = useTheme(eventSettings.theme);
  const dispatch = useDispatch();
  const { accountData, savedQuestions, currentEventFormQuestions } =
    useSelector((state: AppState) => state.account);
  const [addingEmailQuestion, setAddingEmailQuestion] = useState(false);
  const [showConnectGoogleCalendarModal, setShowConnectGoogleCalendarModal] =
    useState(false);

  const isEventOwner = useMemo(
    () => eventSettings.createdBy === accountData.uid,
    [eventSettings.createdBy, accountData.uid]
  );
  const googleCalendarConnected = useMemo(
    () =>
      accountData.googleCalendarInfo.status ===
      IntegrationConnectionStatus.CONNECTED,
    [accountData.googleCalendarInfo.status]
  );

  const goToConnectGoogleCalendar = useCallback(() => {
    window.open(NavigationId.HOME_INTEGRATIONS, "_blank", "noreferrer");
    setShowConnectGoogleCalendarModal(false);
  }, []);

  const renderAdditionalDetailItem = useCallback(
    (
      title: string,
      dropDownMenu: React.ReactNode,
      subtext?: React.ReactNode
    ) => (
      <div
        className="AlignedRowSpaced"
        style={{ padding: "7px 14px", minHeight: 30, gap: 14 }}
      >
        <div className="ColumnNormal" style={{ gap: 4 }}>
          <span className="bodyMedium" style={{ ...theme.PrimaryText }}>
            {title}
          </span>
          {subtext ? (
            <span className="smallBodySubtext" style={{ ...theme.SubText }}>
              {subtext}
            </span>
          ) : null}
        </div>
        {dropDownMenu}
      </div>
    ),
    [theme.PrimaryText, theme.SubText]
  );

  // Dropdown menu item selections
  const visibilityItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Public",
        key: "public",
        onPress: () =>
          updateEventSettings({
            visibility: Visibility.Public,
          }),
      },
      {
        title: "Hidden",
        key: "hidden",
        onPress: () =>
          updateEventSettings({
            visibility: Visibility.Private,
          }),
      },
    ],
    [updateEventSettings]
  );

  const guestListItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Hidden",
        key: "hidden",
        onPress: () =>
          updateEventSettings({
            hideResponses: true,
          }),
      },
      {
        title: "Visible",
        key: "visible",
        onPress: () =>
          updateEventSettings({
            hideResponses: false,
          }),
      },
    ],
    [updateEventSettings]
  );

  const refundItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Show",
        key: "no_refunds",
        onPress: () =>
          updateEventSettings({
            canRefund: false,
          }),
      },
      {
        title: "Hide",
        key: "can_refund",
        onPress: () =>
          updateEventSettings({
            canRefund: true,
          }),
      },
    ],
    [updateEventSettings]
  );

  const tippingItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Disabled",
        key: "tipping_disabled",
        onPress: () =>
          updateEventSettings({
            tippingEnabled: false,
          }),
      },
      {
        title: "Enabled",
        key: "tipping_enabled",
        onPress: () =>
          updateEventSettings({
            tippingEnabled: true,
          }),
      },
    ],
    [updateEventSettings]
  );

  // Turning invites on makes sure the event has an email question to invite guests
  // from (the form builder keeps it from being deleted). Turning them off only
  // stops new invites; the question and the calendar event stay.
  const toggleGoogleCalendarInvites = useCallback(async () => {
    // Toggle off
    if (eventSettings.googleCalendarInvites) {
      updateEventSettings({ googleCalendarInvites: false });
      return;
    }
    // Toggle on
    const hasEmailQuestion = currentEventFormQuestions.some(
      (question) => question.formInput.inputType === FormInputType.EMAIL
    );
    if (!hasEmailQuestion) {
      setAddingEmailQuestion(true);
      try {
        let emailQuestion = savedQuestions.find(
          (question) => question.formInput.inputType === FormInputType.EMAIL
        );
        if (!emailQuestion) {
          const { presetFormQuestions } =
            await API.user.generatePresetSavedFormQuestions({
              uid: accountData.uid,
            });
          dispatch(accountActions.addSavedFormQuestions(presetFormQuestions));
          emailQuestion = presetFormQuestions.find(
            (question) => question.formInput.inputType === FormInputType.EMAIL
          );
        }
        if (!emailQuestion) {
          throw new Error("No email question available");
        }
        dispatch(
          accountActions.updateCurrentEventFormQuestions([emailQuestion])
        );
      } catch (error) {
        console.error(error);
        showNotificationBanner(
          dispatch,
          "Couldn't add the email question. Please try again.",
          NotificationType.NEGATIVE
        );
        return;
      } finally {
        setAddingEmailQuestion(false);
      }
    }
    updateEventSettings({ googleCalendarInvites: true });
  }, [
    accountData.uid,
    currentEventFormQuestions,
    dispatch,
    eventSettings.googleCalendarInvites,
    savedQuestions,
    updateEventSettings,
  ]);

  const renderArrowUpDown = useMemo(
    () => (
      <Icon
        icon={"ion:code"}
        height={18}
        style={{ transform: "rotate(90deg)", ...theme.PrimaryText }}
      />
    ),
    [theme.PrimaryText]
  );

  const renderDivider = useMemo(
    () => (
      <HorizontalDivider
        altMargin={7}
        altWidth={1}
        eventTheme={eventSettings.theme}
      />
    ),
    [eventSettings.theme]
  );

  return (
    <>
      <StandardBorderedContainer
        className="ColumnNormal"
        containerStyles={{
          borderRadius: 20,
          paddingBlock: 7,
          border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
          ...theme.NewPrimaryBG,
        }}
      >
        {renderAdditionalDetailItem(
          "Theme",
          <ThemeDropdownToggle
            visualThemeType={eventSettings.theme}
            themeType={eventSettings.theme}
            onPress={(theme) => updateEventSettings({ theme })}
          />
        )}
        {renderDivider}
        {/* Adjust event visibility */}
        {renderAdditionalDetailItem(
          "Visibility",
          <DropdownMenu
            dropdownItems={visibilityItems}
            trigger={["click"]}
            placement="bottomRight"
            altWidth={150}
          >
            <div
              className="AlignedRowSelect"
              style={{ gap: 4 }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
            >
              {eventSettings.visibility === Visibility.Private ? (
                <div className="AlignedRow" style={{ gap: 4 }}>
                  <Icon
                    icon="ion:eye"
                    height={14}
                    style={{ color: Colors.ORANGE1 }}
                  />
                  <span
                    className="bodyMedium"
                    style={{ color: Colors.ORANGE1 }}
                  >
                    Hidden
                  </span>
                </div>
              ) : (
                <span className="bodyMedium" style={theme.PrimaryText}>
                  Public
                </span>
              )}
              {renderArrowUpDown}
            </div>
          </DropdownMenu>,
          "Can be added to your profile link in bio"
        )}
        {eventSettings.eventType === EventType.MARKIT ? (
          <>
            {renderDivider}
            {/* Adjust guest list visibility */}
            {renderAdditionalDetailItem(
              "Guest List",
              <DropdownMenu
                dropdownItems={guestListItems}
                trigger={["click"]}
                placement="bottomRight"
                altWidth={150}
              >
                <div
                  className="AlignedRowSelect"
                  style={{ gap: 4 }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <span className="bodyMedium" style={theme.PrimaryText}>
                    {eventSettings.hideResponses ? "Hidden" : "Visible"}
                  </span>
                  {renderArrowUpDown}
                </div>
              </DropdownMenu>,
              "Can view guest list upon RSVP"
            )}
            {isEventOwner ? (
              <>
                {renderDivider}
                {renderAdditionalDetailItem(
                  "Google Calendar Invites",
                  googleCalendarConnected ? (
                    <IOSSwitch
                      checked={eventSettings.googleCalendarInvites}
                      disabled={addingEmailQuestion}
                      onChange={toggleGoogleCalendarInvites}
                    />
                  ) : null,
                  googleCalendarConnected ? (
                    "Auto-adds an email question and sends a calendar invite to each guest"
                  ) : (
                    <>
                      Connect to{" "}
                      <span
                        className="bodySmallMedium"
                        style={{ color: Colors.BLUE5, cursor: "pointer" }}
                        onClick={() => setShowConnectGoogleCalendarModal(true)}
                      >
                        Google Calendar
                      </span>
                    </>
                  )
                )}
              </>
            ) : null}
            {eventSettings.customTickets.some(
              (ticket) => ticket.type === TicketType.PAID
            ) ? (
              <>
                {renderDivider}
                {renderAdditionalDetailItem(
                  "No Refunds Banner",
                  <DropdownMenu
                    dropdownItems={refundItems}
                    trigger={["click"]}
                    placement="bottomRight"
                    altWidth={150}
                  >
                    <div
                      className="AlignedRowSelect"
                      style={{ gap: 4 }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "0.7")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                    >
                      <span className="bodyMedium" style={theme.PrimaryText}>
                        {eventSettings.canRefund ? "Hide" : "Show"}
                      </span>
                      {renderArrowUpDown}
                    </div>
                  </DropdownMenu>
                )}
                {renderDivider}
                {renderAdditionalDetailItem(
                  "Enable Tipping",
                  <DropdownMenu
                    dropdownItems={tippingItems}
                    trigger={["click"]}
                    placement="bottomRight"
                    altWidth={150}
                  >
                    <div
                      className="AlignedRowSelect"
                      style={{ gap: 4 }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.opacity = "0.7")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.opacity = "1")
                      }
                    >
                      <span className="bodyMedium" style={theme.PrimaryText}>
                        {eventSettings.tippingEnabled ? "Enabled" : "Disabled"}
                      </span>
                      {renderArrowUpDown}
                    </div>
                  </DropdownMenu>,
                  "Let buyers add a tip at checkout"
                )}
              </>
            ) : null}
          </>
        ) : null}
      </StandardBorderedContainer>
      <ConfirmActionModal
        heading="Connect to Google Calendar"
        subtext="By connecting to Google Calendar, you'll be able to automatically send calendar invites to your guests. This will open a new tab so you don't lose your progress."
        confirmButtonText="Continue"
        icon={<Icon icon="logos:google-calendar" height={35} />}
        hideModal={!showConnectGoogleCalendarModal}
        setIsVisible={setShowConnectGoogleCalendarModal}
        confirmOnPress={goToConnectGoogleCalendar}
      />
    </>
  );
};

export default CreateEventExtraDetails;
